import {
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputLabel,
	Radio,
	RadioGroup,
	Step,
	StepConnector,
	StepLabel,
	Stepper,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import {
	CAMERA_SETUP_STEPS,
	CAMERA_STANDARD_FPS_RANGE,
	CAMERA_STANDARD_RESOLUTION,
	DEFAULT_NUMBER_OF_LINES,
	RTSP_REGEX,
	URL_REGEX
} from "../../../utils/constants";
import {
	useCheckOtsStatusQuery,
	useGetCamerasListQuery,
	useGetSocietyConfigQuery,
	useLazyGetCameraStatusQuery
} from "../../../redux/reducers/settings.reducer";
import { useAppDispatch } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import { LoadingButton } from "@mui/lab";
import CanvasComponent from "../../../common/CameraSetup/CameraCanvas";
import { IAddCameraRequestData, ICameraDetails, IGetCameraStatusRequestData } from "../../../interfaces";
import { useAddCameraMutation } from "../../../redux/reducers/configuration.reducer";
import { useNavigate } from "react-router-dom";
import NumberInput from "../../../common/NumberInput";
import { ICameraCanvasUpdatedDetails, ICanvasComponentRef } from "../../../common/CameraSetup/interfaces";
import { maxBy } from "lodash";
import ConfirmationDialog from "../../../common/ConfirmationDialog";

type RenderStepContentType = {
	[key in number]: ReactNode;
};

interface ICameraFormDetails {
	camera_name: string;
	rtsp: string;
	camera_settings_url: string;
	site_name: string;
	site_id: string;
	camera_id: string;
	camera_location: string;
	secondary_rtsp: string;
	tcp_socket_host: string;
	tcp_socket_port: string;
	drop_frame: string;
}

const AddCamera: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const canvasRef = useRef<ICanvasComponentRef>(null);

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [crossingLinesGeneration, setCrossingLinesGeneration] = useState<"auto" | "manual">("auto");
	const [numberOfLines, setNumberOfLines] = useState<string>(String(DEFAULT_NUMBER_OF_LINES));

	const [showCanvasPreview, setShowCanvasPreview] = useState<boolean>(false);
	const [showPreviewLoader, setShowPreviewLoader] = useState<boolean>(false);
	const [showSubmitButton, setShowSubmitButton] = useState<boolean>(false);
	const [showResolutionConfirmation, setShowResolutionConfirmation] = useState<boolean>(false);
	const [showFPSConfirmation, setShowFPSConfirmation] = useState<boolean>(false);

	// APIS
	const { data: getCamerasListResponse, refetch: refetchCamerasList } = useGetCamerasListQuery({});
	const camerasList = useMemo<ICameraDetails[]>(
		() => getCamerasListResponse?.results ?? [],
		[getCamerasListResponse?.results]
	);

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyConfig = getSocietyConfigResponse?.results ?? [];

	// const { data: siteConfig } = useGetSiteConfigQuery(null);
	const { data: siteConfig } = useCheckOtsStatusQuery(null);

	const [
		addCamera,
		{ isLoading: addCameraLoading, isSuccess: addCameraSuccess, error: addCameraError, reset: resetAddCameraState }
	] = useAddCameraMutation();

	const [
		getCameraImage,
		{
			data: cameraImageData,
			isLoading: getCameraImageLoading,
			isFetching: getCameraImageFetching,
			isSuccess: getCameraImageSuccess,
			error: getCameraImageError
		}
	] = useLazyGetCameraStatusQuery();

	const {
		reset,
		control,
		trigger,
		register,
		getValues,
		handleSubmit,
		formState: { errors }
	} = useForm<ICameraFormDetails>();

	function fetchCameraSample(formData: FieldValues) {
		if (camerasList.length > 0) {
			const existingURLCamera = camerasList.find((cameraItem) => cameraItem.rtsp_uri === formData.rtsp);

			if (existingURLCamera) {
				dispatch(
					showFeedbackNotification({
						message: "Entered RTSP URL already exists",
						severity: "error"
					})
				);

				return;
			}

			const existingSiteIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.site_id) === Number(formData.site_id)
			);

			if (existingSiteIdCamera && existingSiteIdCamera.is_entry !== Boolean(Number(formData.camera_location))) {
				dispatch(
					showFeedbackNotification({
						message: `A camera with same site ID already exists for ${
							existingSiteIdCamera.is_entry ? "entry" : "exit"
						}`,
						severity: "error"
					})
				);

				return;
			}

			const existingSourceIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.camera_id) === Number(formData.camera_id)
			);
			if (existingSourceIdCamera) {
				dispatch(showFeedbackNotification({ message: "Entered Camera ID already exists", severity: "error" }));
				return;
			}
		}

		const requestData: IGetCameraStatusRequestData = {
			primary: {
				rtsp_uri: formData.rtsp,
				fetch_frame: true
			}
		};

		if (formData.secondary_rtsp) {
			requestData.secondary = {
				rtsp_uri: formData.secondary_rtsp,
				fetch_frame: false
			};
		}

		getCameraImage(requestData);
	}

	function handleSubmitCameraSetup(updatedCameraDetails: ICameraCanvasUpdatedDetails) {
		const data = getValues();

		if (camerasList && camerasList.length > 0) {
			const existingSiteIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.site_id) === Number(data.site_id)
			);

			if (existingSiteIdCamera && existingSiteIdCamera.is_entry !== Boolean(Number(data.camera_location))) {
				dispatch(
					showFeedbackNotification({
						message: `A camera with same site ID already exists for ${
							existingSiteIdCamera.is_entry ? "entry" : "exit"
						}`,
						severity: "error"
					})
				);

				return;
			}

			const existingSourceIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.camera_id) === Number(data.camera_id)
			);

			if (existingSourceIdCamera) {
				dispatch(
					showFeedbackNotification({
						message: "Entered Camera ID already exists",
						severity: "error"
					})
				);

				return;
			}
		}

		const addCameraDetails: IAddCameraRequestData = {
			rtsp_uri: data.rtsp,
			// camera_site: data.camera_name,
			is_entry: Boolean(Number(data.camera_location)),
			roi: updatedCameraDetails.roi,
			line: updatedCameraDetails.line,
			society: societyConfig && societyConfig.length > 0 ? societyConfig[0].id : 1,
			width: cameraImageData?.primary?.image_data?.width ?? updatedCameraDetails.width,
			height: cameraImageData?.primary?.image_data?.height ?? updatedCameraDetails.height,
			site_id: Number(data.site_id),
			camera_id: Number(data.camera_id),
			site_name: data.camera_name ?? "",
			camera_page_url: data.camera_settings_url,
			secondary_stream_rtsp_uri: data.secondary_rtsp || null,
			tcp_socket_host: data.tcp_socket_host || null,
			tcp_socket_port: data.tcp_socket_port || null,
			drop_frame: Number(data.drop_frame)
		};

		addCamera(addCameraDetails);
	}

	function handleNextButtonClick(data: FieldValues) {
		switch (currentStep) {
			case 0:
				trigger().then((isFormValid) => {
					if (isFormValid) fetchCameraSample(data);
				});

				break;

			case 1:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(2);
				break;

			case 2:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(3);
				break;

			case 3:
				break;

			default:
				break;
		}
	}

	function handleBackButtonClick() {
		switch (currentStep) {
			case 0:
				navigate(-1);
				break;

			case 1:
				setCurrentStep(0);
				break;

			case 2:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(1);
				break;

			case 3:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(2);
				break;

			default:
				break;
		}
	}

	function handleChangeCrossingLinesGeneration(updatedValue: "auto" | "manual") {
		setCrossingLinesGeneration(updatedValue);
		setShowSubmitButton(false);
		if (updatedValue === "auto") setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
	}

	function handleChangeNumberOfLines(updatedValue: string) {
		setNumberOfLines(updatedValue);
		setShowSubmitButton(false);
	}

	function handleUndoButtonClick() {
		if (canvasRef.current) canvasRef.current.undoCanvasDrawing();
	}

	function handleResetButtonClick() {
		if (canvasRef.current) canvasRef.current.resetCanvas();
		setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
		setCrossingLinesGeneration("auto");
	}

	function handleEditButtonClick() {
		if (canvasRef.current) canvasRef.current.editCanvas();
	}

	function handlePreviewButtonClick() {
		if (canvasRef.current) canvasRef.current.previewCameraCanvas();
	}

	function handleSaveButtonClick() {
		if (canvasRef.current) canvasRef.current.submitCameraCanvas();
	}

	function isNextButtonLoading(): boolean {
		switch (currentStep) {
			case 0:
				return getCameraImageLoading || getCameraImageFetching;

			default:
				return showPreviewLoader;
		}
	}

	function handleOpenFPSConfirmationDialog() {
		setShowFPSConfirmation(true);
	}

	function handleCloseFPSConfirmationDialog() {
		setShowFPSConfirmation(false);
	}

	function handleConfirmCameraFPS() {
		handleCloseFPSConfirmationDialog();
		setCurrentStep(1);
	}

	function handleOpenResolutionConfirmationDialog() {
		setShowResolutionConfirmation(true);
	}

	function handleCloseResolutionConfirmationDialog() {
		setShowResolutionConfirmation(false);
	}

	function handleConfirmCameraResolution() {
		if (cameraImageData && cameraImageData.primary && cameraImageData.primary.image_data) {
			handleCloseResolutionConfirmationDialog();

			if (
				cameraImageData.primary.image_data.fps < CAMERA_STANDARD_FPS_RANGE.min ||
				cameraImageData.primary.image_data.fps > CAMERA_STANDARD_FPS_RANGE.max
			) {
				handleOpenFPSConfirmationDialog();
			} else {
				setCurrentStep(1);
			}
		}
	}

	useEffect(() => {
		let siteId = "1";
		let cameraId = "1";

		const maxSiteIdDetails = maxBy(camerasList, "site_id");
		const maxCameraIdDetails = maxBy(camerasList, "camera_id");

		if (maxSiteIdDetails) siteId = String(Number(maxSiteIdDetails.site_id) + 1);
		if (maxCameraIdDetails) cameraId = String(Number(maxCameraIdDetails.camera_id) + 1);

		reset({
			camera_location: "1",
			camera_name: "",
			site_id: siteId,
			camera_id: cameraId,
			rtsp: "",
			camera_settings_url: "",
			secondary_rtsp: "",
			site_name: "",
			tcp_socket_host: "",
			tcp_socket_port: ""
		});
	}, [camerasList, reset]);

	useEffect(() => {
		if (getCameraImageSuccess && cameraImageData) {
			if (cameraImageData.primary && cameraImageData.primary.status && cameraImageData.primary.image_data) {
				if (!cameraImageData.secondary || (cameraImageData.secondary && cameraImageData.secondary.status)) {
					if (
						cameraImageData.primary.image_data.width !== CAMERA_STANDARD_RESOLUTION.width ||
						cameraImageData.primary.image_data.height !== CAMERA_STANDARD_RESOLUTION.height
					) {
						handleOpenResolutionConfirmationDialog();
					} else if (
						cameraImageData.primary.image_data.fps < CAMERA_STANDARD_FPS_RANGE.min ||
						cameraImageData.primary.image_data.fps > CAMERA_STANDARD_FPS_RANGE.max
					) {
						handleOpenFPSConfirmationDialog();
					} else {
						setCurrentStep(1);
					}
				} else {
					dispatch(
						showFeedbackNotification({
							message: "Secondary RTSP camera not found",
							severity: "error"
						})
					);
				}
			} else {
				dispatch(
					showFeedbackNotification({
						message: "Image not found",
						severity: "error"
					})
				);
			}
		}
	}, [getCameraImageSuccess, cameraImageData, dispatch]);

	useEffect(() => {
		if (getCameraImageError && "data" in getCameraImageError) {
			dispatch(
				showFeedbackNotification({
					message: getCameraImageError.data ? String(getCameraImageError.data) : "Something went wrong",
					severity: "error"
				})
			);
		}
	}, [dispatch, getCameraImageError]);

	useEffect(() => {
		if (addCameraSuccess) {
			resetAddCameraState();
			refetchCamerasList();
			navigate("/ots/cameras-list");
		}
	}, [addCameraSuccess, navigate, refetchCamerasList, resetAddCameraState]);

	useEffect(() => {
		if (crossingLinesGeneration === "auto") {
			setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
		}
	}, [crossingLinesGeneration]);

	useEffect(() => {
		if (addCameraError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in addCameraError && addCameraError.data ? String(addCameraError.data) : "Failed to add camera",
					severity: "error"
				})
			);

			resetAddCameraState();
		}
	}, [addCameraError, dispatch, resetAddCameraState]);

	const RenderStepContent: RenderStepContentType = {
		0: (
			<Box className="ots-add-camera-form-step">
				<Grid container rowSpacing={4} columnSpacing={6}>
					<Grid item xs={12}>
						<InputLabel
							required
							htmlFor="camera-location-input"
							error={!!errors.camera_location}
							classes={{ root: "ots-input-label-root" }}
						>
							Camera Direction
						</InputLabel>

						<FormControl fullWidth sx={{ marginTop: "1rem" }}>
							<Controller
								name="camera_location"
								control={control}
								defaultValue="1"
								rules={{ required: "Camera Direction is required", onChange: () => trigger("site_id") }}
								render={({ field: { value, onChange } }) => (
									<RadioGroup row sx={{ gap: "2rem" }}>
										<FormControlLabel
											value={1}
											control={<Radio />}
											label="Entry"
											checked={Number(value) === 1}
											onChange={(_, checked) => onChange(checked ? 1 : 0)}
										/>

										<FormControlLabel
											value={0}
											control={<Radio />}
											label="Exit"
											checked={Number(value) === 0}
											onChange={(_, checked) => onChange(checked ? 0 : 1)}
										/>
									</RadioGroup>
								)}
							/>

							{errors.camera_location && errors.camera_location.message ? (
								<FormHelperText error>{errors.camera_location.message.toString()}</FormHelperText>
							) : null}
						</FormControl>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="camera-name-input"
							error={!!errors.camera_name}
							classes={{ root: "ots-input-label-root" }}
						>
							Camera Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="camera-name-input"
							placeholder="Enter the Camera Name"
							autoComplete="off"
							error={!!errors.camera_name}
							helperText={errors.camera_name && errors.camera_name.message ? errors.camera_name.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("camera_name", { required: "Camera Name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="camera-settings-url-input"
							error={!!errors.camera_settings_url}
							classes={{ root: "ots-input-label-root" }}
						>
							Camera Settings URL
						</InputLabel>

						<TextField
							fullWidth
							variant="filled"
							color="primary"
							id="camera-settings-url-input"
							placeholder="Enter the Settings URL"
							autoComplete="off"
							error={!!errors.camera_settings_url}
							helperText={
								errors.camera_settings_url && errors.camera_settings_url.message
									? errors.camera_settings_url.message.toString()
									: ""
							}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("camera_settings_url", { pattern: { value: URL_REGEX, message: "Enter a vaild URL" } })}
						/>
					</Grid>

					{/* <Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="site-name-input"
							error={!!errors.site_name}
							classes={{ root: "ots-input-label-root" }}
						>
							Site Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="site-name-input"
							placeholder="Enter the Site Name"
							error={!!errors.site_name}
							helperText={errors.site_name && errors.site_name.message ? errors.site_name.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("site_name", { required: "Site Name is required" })}
						/>
					</Grid> */}

					{/* {siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME ? ( */}
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="site-id-input"
							error={!!errors.site_id}
							classes={{ root: "ots-input-label-root" }}
						>
							Site ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							variant="filled"
							color="primary"
							id="site-id-input"
							placeholder="Enter the Site ID"
							error={!!errors.site_id}
							helperText={errors.site_id && errors.site_id.message ? errors.site_id.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("site_id", {
								required: "Site ID is required",
								min: { value: 0, message: "Site ID cannot be negative" },
								validate: (value, formValues) => {
									const existingSiteIdCamera = camerasList.find(
										(cameraItem) => Number(cameraItem.site_id) === Number(value)
									);

									if (
										existingSiteIdCamera &&
										existingSiteIdCamera.is_entry !== Boolean(Number(formValues.camera_location))
									) {
										return `A camera with same site ID already exists for ${
											existingSiteIdCamera.is_entry ? "entry" : "exit"
										}`;
									}

									return true;
								},
								onChange: () => trigger("site_id")
							})}
						/>
					</Grid>
					{/* ) : null} */}

					{/* {siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME ? ( */}
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="source-id-input"
							error={!!errors.camera_id}
							classes={{ root: "ots-input-label-root" }}
						>
							Camera ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							variant="filled"
							color="primary"
							id="source-id-input"
							placeholder="Enter the Camera ID"
							error={!!errors.camera_id}
							helperText={errors.camera_id && errors.camera_id.message ? errors.camera_id.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("camera_id", {
								required: "Camera ID is required",
								min: { value: 0, message: "Camera ID cannot be negative" },
								validate: (value) => {
									const existingSourceIdCamera = camerasList.find(
										(cameraItem) => Number(cameraItem.camera_id) === Number(value)
									);

									if (existingSourceIdCamera) {
										return "A camera with this source ID already exists";
									}

									return true;
								},
								onChange: () => trigger("camera_id")
							})}
						/>
					</Grid>
					{/* ) : null} */}

					<Grid item xs={12} md={6}>
						<InputLabel required htmlFor="rtsp-input" error={!!errors.rtsp} classes={{ root: "ots-input-label-root" }}>
							RTSP
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="filled"
							color="primary"
							id="rtsp-input"
							placeholder="Enter the RTSP Link"
							autoComplete="off"
							error={!!errors.rtsp}
							helperText={errors.rtsp && errors.rtsp.message ? errors.rtsp.message.toString() : ""}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							{...register("rtsp", {
								required: "RTSP Link is required",
								validate: (value) => {
									if (!value) value = "";

									if (value.startsWith("rtsp://")) {
										if (!RTSP_REGEX.test(value)) {
											return "Enter a valid RTSP URL";
										}

										return true;
									}

									if (siteConfig && siteConfig.default_econ_path !== value) {
										return "Enter a valid RTSP URL";
									}

									return true;
								}
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="secondary-rtsp-input"
							error={!!errors.secondary_rtsp}
							classes={{ root: "ots-input-label-root" }}
						>
							Secondary RTSP
						</InputLabel>

						<TextField
							fullWidth
							variant="filled"
							color="primary"
							id="secondary-rtsp-input"
							placeholder="Enter Secondary RTSP Link"
							autoComplete="off"
							error={!!errors.secondary_rtsp}
							helperText={
								errors.secondary_rtsp && errors.secondary_rtsp.message ? errors.secondary_rtsp.message.toString() : ""
							}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "ots-input-root" } }}
							{...register("secondary_rtsp", {
								validate: (value) => {
									if (value) {
										if (value.startsWith("rtsp://")) {
											if (!RTSP_REGEX.test(value)) {
												return "Enter a valid RTSP URL";
											}

											return true;
										}

										if (siteConfig && siteConfig.default_econ_path !== value) {
											return "Enter a valid RTSP URL";
										}
									}

									return true;
								}
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="tcp-socket-host-input"
							error={!!errors.tcp_socket_host}
							classes={{ root: "ots-input-label-root" }}
						>
							TCP Socket Host
						</InputLabel>

						<TextField
							fullWidth
							variant="filled"
							color="primary"
							id="tcp-socket-host-input"
							placeholder="Enter the TCP Socket Host"
							autoComplete="off"
							error={!!errors.tcp_socket_host}
							helperText={
								errors.tcp_socket_host && errors.tcp_socket_host.message
									? errors.tcp_socket_host.message.toString()
									: ""
							}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "ots-input-root" } }}
							{...register("tcp_socket_host")}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="tcp-socket-port-input"
							error={!!errors.tcp_socket_port}
							classes={{ root: "ots-input-label-root" }}
						>
							TCP Socket Port
						</InputLabel>

						<TextField
							fullWidth
							variant="filled"
							color="primary"
							id="tcp-socket-port-input"
							placeholder="enter the TCP Socket Port"
							autoComplete="off"
							error={!!errors.tcp_socket_port}
							helperText={
								errors.tcp_socket_port && errors.tcp_socket_port.message
									? errors.tcp_socket_port.message.toString()
									: ""
							}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "ots-input-root" } }}
							{...register("tcp_socket_port")}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							htmlFor="drop-frame-input"
							error={!!errors.drop_frame}
							classes={{ root: "ots-input-label-root" }}
						>
							Drop Frame
						</InputLabel>

						<NumberInput
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							variant="filled"
							color="primary"
							id="drop-frame-input"
							placeholder="Enter the drop frame"
							autoComplete="off"
							error={!!errors.drop_frame}
							helperText={errors.drop_frame && errors.drop_frame.message ? errors.drop_frame.message.toString() : ""}
							InputProps={{ hiddenLabel: true, disableUnderline: true, classes: { root: "ots-input-root" } }}
							{...register("drop_frame")}
						/>
					</Grid>
				</Grid>
			</Box>
		),
		1: (
			<Box className="ots-add-camera-form-step">
				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Select Region of Interest
					</Typography>

					<Typography variant="body2" color="#616161">
						Plot the dot on the stream to select the region of interest
					</Typography>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "100px" }}>
					{showCanvasPreview ? (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<EditIcon />}
							onClick={handleEditButtonClick}
						>
							Edit
						</Button>
					) : (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<RestartAltOutlinedIcon />}
							onClick={handleUndoButtonClick}
						>
							Undo
						</Button>
					)}

					<Button
						variant="outlined"
						color="primary"
						size="small"
						startIcon={<CloseIcon />}
						onClick={handleResetButtonClick}
					>
						Reset
					</Button>
				</Box>
			</Box>
		),
		2: (
			<Box className="ots-add-camera-form-step">
				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Define the Direction Arrow
					</Typography>

					<Typography variant="body2" color="#616161">
						Draw an arrow in a way where vehicles will approach the camera
					</Typography>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "100px" }}>
					{showCanvasPreview ? (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<EditIcon />}
							onClick={handleEditButtonClick}
						>
							Edit
						</Button>
					) : (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<RestartAltOutlinedIcon />}
							onClick={handleUndoButtonClick}
						>
							Undo
						</Button>
					)}

					<Button
						variant="outlined"
						color="primary"
						size="small"
						startIcon={<CloseIcon />}
						onClick={handleResetButtonClick}
					>
						Reset
					</Button>
				</Box>
			</Box>
		),
		3: (
			<Box className="ots-add-camera-form-step">
				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Setup Crossing Lines
					</Typography>

					<Typography variant="body2" color="#616161">
						Setup the crossing lines (each time a vehicle crosses a line, the number plate is read).
					</Typography>
				</Box>

				<FormControl fullWidth>
					<RadioGroup>
						<FormControlLabel
							value="auto"
							control={<Radio />}
							label="Automatic"
							disabled={showCanvasPreview}
							checked={crossingLinesGeneration === "auto"}
							onChange={(event) =>
								handleChangeCrossingLinesGeneration((event.target as HTMLInputElement).value as "auto" | "manual")
							}
						/>

						<FormControlLabel
							value="manual"
							control={<Radio />}
							label="Manual"
							disabled={showCanvasPreview}
							checked={crossingLinesGeneration === "manual"}
							onChange={(event) =>
								handleChangeCrossingLinesGeneration((event.target as HTMLInputElement).value as "auto" | "manual")
							}
						/>

						<NumberInput
							positiveOnly
							disableSigned
							disableDecimal
							autoComplete="new-password"
							variant="filled"
							size="small"
							placeholder="Add number of lines"
							disabled={crossingLinesGeneration !== "manual" || showCanvasPreview}
							value={numberOfLines}
							onChange={(event) => handleChangeNumberOfLines(event.target.value)}
							InputProps={{
								hiddenLabel: true,
								disableUnderline: true,
								classes: { root: "ots-input-root" }
							}}
							sx={{ maxWidth: "250px" }}
						/>
					</RadioGroup>
				</FormControl>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "100px" }}>
					{showCanvasPreview ? (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<EditIcon />}
							onClick={handleEditButtonClick}
						>
							Edit
						</Button>
					) : (
						<Button
							disableElevation
							variant="contained"
							color="primary"
							size="small"
							startIcon={<RestartAltOutlinedIcon />}
							onClick={handleUndoButtonClick}
						>
							Undo
						</Button>
					)}

					<Button
						variant="outlined"
						color="primary"
						size="small"
						startIcon={<CloseIcon />}
						onClick={handleResetButtonClick}
					>
						Reset
					</Button>
				</Box>
			</Box>
		)
	};

	return (
		<Box
			className="ots-screen-content-wrapper"
			component="form"
			noValidate
			onSubmit={handleSubmit(handleNextButtonClick)}
		>
			<Box className="ots-add-camera-screen-content">
				<Stepper
					alternativeLabel
					activeStep={currentStep}
					connector={
						<StepConnector
							classes={{
								line: "camera-setup-stepper-connector-line",
								root: "camera-setup-stepper-connector-root",
								completed: "camera-setup-stepper-connector-completed",
								active: "camera-setup-stepper-connector-completed"
							}}
						/>
					}
				>
					<Step classes={{ root: "camera-setup-step-root" }}>
						<StepLabel
							classes={{
								iconContainer: "camera-setup-step-label-icon-container",
								disabled: "camera-setup-step-label-disabled"
							}}
						>
							Setup Camera Details
						</StepLabel>
					</Step>

					{CAMERA_SETUP_STEPS.map((stepItem) => (
						<Step key={stepItem.id} classes={{ root: "camera-setup-step-root" }}>
							<StepLabel
								classes={{
									iconContainer: "camera-setup-step-label-icon-container",
									disabled: "camera-setup-step-label-disabled"
								}}
							>
								{stepItem.label}
							</StepLabel>
						</Step>
					))}
				</Stepper>

				<Grid container spacing={2} sx={{ flexGrow: 1 }}>
					{currentStep >= 1 ? (
						<Grid item xs={12} md={7}>
							<Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
								{cameraImageData &&
								cameraImageData.primary &&
								cameraImageData.primary.image_data &&
								cameraImageData.primary.image_data.image ? (
									<CanvasComponent
										ref={canvasRef}
										image={cameraImageData.primary.image_data.image}
										// width={cameraImageData.primary.image_data.width}
										// height={cameraImageData.primary.image_data.height}
										numberOfLines={Number(numberOfLines)}
										cameraDetails={{
											id: -1,
											rtsp_uri: "",
											camera_site: "",
											rtmp_uri: "",
											roi: "",
											line: "",
											zoom: 0,
											is_entry: false,
											society: 1,
											width: 0,
											height: 0,
											num_lines: DEFAULT_NUMBER_OF_LINES,
											site_id: -1,
											site_name: "",
											camera_id: -1,
											camera_page_url: "",
											secondary_stream_rtsp_uri: "",
											tcp_socket_host: "",
											tcp_socket_port: "",
											drop_frame: 0,
											camera_type: null
										}}
										rtspUrlInput=""
										onSaveDetails={handleSubmitCameraSetup}
										setCurrentCanvasStep={setCurrentStep}
										setCrossingLinesGeneration={setCrossingLinesGeneration}
										setShowCanvasPreview={setShowCanvasPreview}
										setShowPreviewLoader={setShowPreviewLoader}
										setShowSubmitButton={setShowSubmitButton}
									/>
								) : null}
							</Box>
						</Grid>
					) : null}

					<Grid item xs={12} md={currentStep < 1 ? 12 : 5}>
						<Box className="ots-add-camera-form-wrapper">{RenderStepContent[currentStep]}</Box>
					</Grid>
				</Grid>
			</Box>

			<Box className="ots-form-actions-wrapper">
				<Button variant="outlined" color="error" sx={{ paddingX: 5 }} onClick={handleBackButtonClick}>
					Back
				</Button>

				{currentStep < CAMERA_SETUP_STEPS.length ? (
					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						type="submit"
						loading={isNextButtonLoading()}
						sx={{ paddingX: 5 }}
					>
						Next
					</LoadingButton>
				) : showSubmitButton ? (
					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						// type="submit"
						loading={addCameraLoading}
						onClick={handleSaveButtonClick}
					>
						Save and Next
					</LoadingButton>
				) : (
					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ArrowRightIcon />}
						onClick={handlePreviewButtonClick}
						loading={showPreviewLoader}
						loadingPosition="start"
					>
						Preview
					</LoadingButton>
				)}
			</Box>

			{/* CAMERA NON-STANDARD RESOLUTION CONFIRMATION DIALOG */}
			<ConfirmationDialog
				width="sm"
				color="primary"
				title="Confirm Camera Resolution"
				heading={`Adding a camera that is not ${CAMERA_STANDARD_RESOLUTION.width} x ${CAMERA_STANDARD_RESOLUTION.height}. Are you sure you want to proceed?`}
				open={showResolutionConfirmation}
				onClose={handleCloseResolutionConfirmationDialog}
				onConfirm={handleConfirmCameraResolution}
			/>

			{/* CAMERA NON-STANDARD FPS CONFIRMATION DAILOG */}
			<ConfirmationDialog
				width="sm"
				color="primary"
				title="Confirm Camera FPS"
				heading={`Adding a camera that is not ${CAMERA_STANDARD_FPS_RANGE.min}-${CAMERA_STANDARD_FPS_RANGE.max}. Are you sure you want to proceed?`}
				open={showFPSConfirmation}
				onClose={handleCloseFPSConfirmationDialog}
				onConfirm={handleConfirmCameraFPS}
			/>
		</Box>
	);
};

export default AddCamera;
